import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useRef, useState, createContext, useContext } from 'react';
import LoadingScreen from '../components/LoadingScreen';

// Create Audio Context
interface AudioContextType {
  audioRef: React.MutableRefObject<HTMLAudioElement | null>;
  isPlaying: boolean;
  volume: number;
  isMuted: boolean;
  togglePlayPause: () => void;
  toggleMute: () => void;
  setVolume: (vol: number) => void;
}

const AudioContext = createContext<AudioContextType | undefined>(undefined);

export const useAudio = () => {
  const context = useContext(AudioContext);
  if (!context) {
    throw new Error('useAudio must be used within AudioProvider');
  }
  return context;
};

export const AudioProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(10); // Start at 10%
  const [isMuted, setIsMuted] = useState(false);

  useEffect(() => {
    // Start playing music on mount
    if (audioRef.current) {
      audioRef.current.volume = volume / 100;
      audioRef.current.play().catch(err => console.log('Autoplay prevented:', err));
      setIsPlaying(true);
    }
  }, []);

  useEffect(() => {
    // Update audio volume
    if (audioRef.current) {
      audioRef.current.volume = isMuted ? 0 : volume / 100;
    }
  }, [volume, isMuted]);

  const togglePlayPause = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        audioRef.current.play();
        setIsPlaying(true);
      }
    }
  };

  const toggleMute = () => {
    setIsMuted(!isMuted);
  };

  return (
    <AudioContext.Provider value={{ audioRef, isPlaying, volume, isMuted, togglePlayPause, toggleMute, setVolume }}>
      {children}
      <audio ref={audioRef} src="/music/wellness-dass-music.mp3" loop />
    </AudioContext.Provider>
  );
};

// Helper function to get appropriate background image
const getBackgroundImage = () => {
  return window.innerWidth >= 1024
    ? 'url(/BackgroundSamples/welness-dass-walpaper-desktop.png)'
    : 'url(/BackgroundSamples/welness-dass-wallpaper.png)';
};

// Helper function to cache images with size limit and error handling
const cacheImageToIndexedDB = async (imageUrl: string) => {
  try {
    const response = await fetch(imageUrl);
    const blob = await response.blob();
    
    // Don't cache if file is too large (> 1MB)
    if (blob.size > 1024 * 1024) {
      console.warn(`Image ${imageUrl} is too large to cache (${blob.size} bytes)`);
      return;
    }
    
    const reader = new FileReader();
    reader.onloadend = () => {
      try {
        const base64data = reader.result;
        localStorage.setItem(`dass_image_cache_${imageUrl}`, base64data as string);
      } catch (error) {
        // Silently handle quota exceeded errors
        if (error instanceof Error && error.message.includes('QuotaExceededError')) {

          // Clear old cached images to make space
          const keys = Object.keys(localStorage);
          const cacheKeys = keys.filter(k => k.startsWith('dass_image_cache_'));
          if (cacheKeys.length > 0) {
            localStorage.removeItem(cacheKeys[0]);
          }
        }
      }
    };
    reader.readAsDataURL(blob);
  } catch (error) {

  }
};

// Helper function to get cached or load image
const getImageUrl = (imageUrl: string): string => {
  try {
    const cached = localStorage.getItem(`dass_image_cache_${imageUrl}`);
    if (cached) {
      return cached;
    }
  } catch (error) {
    // Ignore storage access errors

  }
  
  // Cache the image for future loads
  cacheImageToIndexedDB(imageUrl);
  return imageUrl;
};

// Helper function to clean up old image cache
const clearOldImageCache = () => {
  try {
    const keys = Object.keys(localStorage);
    const cacheKeys = keys.filter(k => k.startsWith('dass_image_cache_'));
    
    // Keep only the most recent 2 cached images, remove the rest
    if (cacheKeys.length > 2) {
      cacheKeys.slice(0, -2).forEach(key => {
        localStorage.removeItem(key);
      });
    }
  } catch (error) {

  }
};

const DassPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isPlaying, volume, isMuted, togglePlayPause, toggleMute, setVolume } = useAudio();
  const [imagesLoaded, setImagesLoaded] = useState(false);

  // Preload and cache images on component mount
  useEffect(() => {
    // Clear old cache before preloading new images
    clearOldImageCache();
    
    const preloadImages = async () => {
      const desktopBg = '/BackgroundSamples/welness-dass-walpaper-desktop.png';
      const mobileBg = '/BackgroundSamples/welness-dass-wallpaper.png';
      
      try {
        // Check if images are cached, if not cache them
        const cached1 = localStorage.getItem(`dass_image_cache_${desktopBg}`);
        const cached2 = localStorage.getItem(`dass_image_cache_${mobileBg}`);
        
        if (!cached1) {
          await cacheImageToIndexedDB(desktopBg);
        }
        if (!cached2) {
          await cacheImageToIndexedDB(mobileBg);
        }
        
        setImagesLoaded(true);
      } catch (error) {

        // Still set loaded even if error to prevent infinite loading
        setImagesLoaded(true);
      }
    };

    preloadImages();
  }, []);

  if (!imagesLoaded) {
    return <LoadingScreen message="Loading assessment..." fullScreen />;
  }

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setVolume(Number(e.target.value));
  };

  const handleTakeAssessment = () => {
    // Check if we're in the panel context (logged in user)
    if (location.pathname.startsWith('/panel')) {
      navigate('/panel/wellness/dass/assessment'); // Navigate to DASS assessment within panel
    } else {
      navigate('/wellness/dass/assessment'); // Absolute navigation for public route
    }
  };

  return (
    <div className="dass-page" style={{ backgroundImage: getBackgroundImage() }}>
      <div className="dass-page__content">
        <header className="dass-page__header">
          <button
            className="dass-page__home-btn"
            onClick={() => navigate('/panel/learn/')}
          >
            Home
          </button>
        </header>
        <main className="dass-page__main">
          <h1 className="dass-page__title">
            Rate Your Mental Health in Just 3 Minutes
          </h1>
          <p className="dass-page__description">
            Discover your DASS profile – and learn which areas of your mental well-being are thriving, and which could use a boost.
          </p>
          <button className="dass-page__cta-button" onClick={handleTakeAssessment}>
            Take the Assessment
          </button>
        </main>

        <div className="dass-page__audio-controls">
          <button className="dass-page__audio-btn" onClick={togglePlayPause}><span className="material-symbols-outlined">{isPlaying ? 'pause' : 'play_arrow'}</span></button>
          <button className="dass-page__audio-btn" onClick={toggleMute}><span className="material-symbols-outlined">{isMuted ? 'volume_off' : 'volume_up'}</span></button>
          <span className="dass-page__volume-text">Volume</span>
          <div className="dass-page__volume-bar">
            <input
              type="range"
              min="0"
              max="100"
              value={volume}
              onChange={handleVolumeChange}
              className="dass-page__volume-fill"
              style={{ '--volume': `${volume}%` } as React.CSSProperties}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default DassPage;


